import { Routes, Route } from '@angular/router';
import { personaGuard } from '@core/guard/persona.guard';
import { authGuard, inviteCreateGuard } from '@core/guard/auth.guard';

// Phase 1: every feature route renders the shared placeholder. Later phases replace the
// `loadComponent` per screen. `title` drives the topbar; `screen` maps to the mockup id.
const placeholder = () =>
  import('@modules/shared/placeholder/placeholder.component').then(m => m.PlaceholderComponent);

const ph = (path: string, title: string, screen: string): Route =>
  ({ path, loadComponent: placeholder, data: { title, screen } });

export const routes: Routes = [
  // PUBLIC
  {
    path: 'auth/login',
    loadComponent: () => import('@modules/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    // Login-free document re-upload, opened from a WhatsApp link (?token=…). No guard.
    path: 'reupload',
    loadComponent: () => import('@modules/auth/reupload/reupload.component').then(m => m.ReuploadComponent),
    data: { title: 'Re-upload documents', screen: 'auth' },
  },

  // ROOT — land any visitor on the public customer marketplace.
  { path: '', redirectTo: '/customer/discover', pathMatch: 'full' },

  // VENDOR
  {
    path: 'vendor',
    canActivate: [personaGuard(['vendor'])],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () => import('@modules/vendor/dashboard/vendor-dashboard.component').then(m => m.VendorDashboardComponent),
        data: { title: 'Dashboard' },
      },
      {
        path: 'bookings',
        loadComponent: () => import('@modules/vendor/bookings/vendor-bookings.component').then(m => m.VendorBookingsComponent),
        data: { title: 'Bookings', screen: 'v-booking' },
      },
      {
        path: 'bookings/new',
        loadComponent: () => import('@modules/vendor/new-booking/vendor-new-booking.component').then(m => m.VendorNewBookingComponent),
        data: { title: 'New booking', screen: 'v-newbooking' },
      },
      {
        path: 'bookings/:id',
        loadComponent: () => import('@modules/vendor/booking-detail/vendor-booking-detail.component').then(m => m.VendorBookingDetailComponent),
        data: { title: 'Booking', screen: 'v-bkdetail' },
      },
      {
        path: 'services',
        loadComponent: () => import('@modules/vendor/services/vendor-services.component').then(m => m.VendorServicesComponent),
        data: { title: 'My Services', screen: 'v-services' },
      },
      {
        path: 'services/:id',
        loadComponent: () => import('@modules/vendor/offering/vendor-offering.component').then(m => m.VendorOfferingComponent),
        data: { title: 'Package', screen: 'v-offering' },
      },
      {
        path: 'website',
        loadComponent: () => import('@modules/vendor/website/vendor-website.component').then(m => m.VendorWebsiteComponent),
        data: { title: 'Website', screen: 'v-vsite' },
      },
      {
        path: 'partners',
        loadComponent: () => import('@modules/vendor/partners/vendor-partners.component').then(m => m.VendorPartnersComponent),
        data: { title: 'Partnerships', screen: 'v-partners' },
      },
      {
        path: 'customers',
        loadComponent: () => import('@modules/vendor/customers/vendor-customers.component').then(m => m.VendorCustomersComponent),
        data: { title: 'Customers', screen: 'v-customers' },
      },
      {
        path: 'customers/:id',
        loadComponent: () => import('@modules/vendor/customer-detail/vendor-customer-detail.component').then(m => m.VendorCustomerDetailComponent),
        data: { title: 'Customer', screen: 'v-custdetail' },
      },
      {
        path: 'wallet',
        loadComponent: () => import('@modules/vendor/wallet/vendor-wallet.component').then(m => m.VendorWalletComponent),
        data: { title: 'Wallet', screen: 'v-wallet' },
      },
      {
        path: 'profile',
        loadComponent: () => import('@modules/vendor/profile/vendor-profile.component').then(m => m.VendorProfileComponent),
        data: { title: 'Profile', screen: 'v-profile' },
      },
      {
        path: 'onboard',
        loadComponent: () => import('@modules/vendor/onboard/vendor-onboard.component').then(m => m.VendorOnboardComponent),
        data: { title: 'Onboard vendor', screen: 'm-onboard' },
      },
    ],
  },

  // ADMIN
  {
    path: 'admin',
    canActivate: [personaGuard(['admin'])],
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        loadComponent: () => import('@modules/admin/overview/admin-overview.component').then(m => m.AdminOverviewComponent),
        data: { title: 'Overview', screen: 'a-console' },
      },
      {
        path: 'vendors',
        loadComponent: () => import('@modules/admin/vendors/admin-vendors.component').then(m => m.AdminVendorsComponent),
        data: { title: 'Vendors', screen: 'a-console' },
      },
      {
        // Admin-only entry to onboard a NEW vendor business (platform-operator action).
        // Must precede 'vendors/:id' so the literal wins over the :id param.
        path: 'vendors/onboard',
        loadComponent: () => import('@modules/vendor/onboard/vendor-onboard.component').then(m => m.VendorOnboardComponent),
        data: { title: 'Onboard vendor', screen: 'm-onboard' },
      },
      {
        path: 'vendors/:id',
        loadComponent: () => import('@modules/admin/vendor-detail/admin-vendor-detail.component').then(m => m.AdminVendorDetailComponent),
        data: { title: 'Vendor', screen: 'a-console' },
      },
      {
        path: 'customers',
        loadComponent: () => import('@modules/admin/customers/admin-customers.component').then(m => m.AdminCustomersComponent),
        data: { title: 'Customers', screen: 'a-console' },
      },
      {
        path: 'customers/:id',
        loadComponent: () => import('@modules/admin/customer-detail/admin-customer-detail.component').then(m => m.AdminCustomerDetailComponent),
        data: { title: 'Customer', screen: 'a-console' },
      },
      {
        path: 'finance',
        loadComponent: () => import('@modules/admin/finance/admin-finance.component').then(m => m.AdminFinanceComponent),
        data: { title: 'Finance', screen: 'a-console' },
      },
      {
        path: 'whatsapp',
        loadComponent: () => import('@modules/admin/whatsapp/admin-whatsapp.component').then(m => m.AdminWhatsappComponent),
        data: { title: 'WhatsApp', screen: 'a-console' },
      },
    ],
  },

  // CUSTOMER — publicly viewable (no persona guard): any visitor can browse the whole
  // module. Only the login-gated CREATE flows carry an authGuard (see book/:id below);
  // the "Request to book" and invite-create actions gate in-component.
  {
    path: 'customer',
    children: [
      { path: '', redirectTo: 'discover', pathMatch: 'full' },
      {
        path: 'discover',
        loadComponent: () => import('@modules/customer/discover/customer-discover.component').then(m => m.CustomerDiscoverComponent),
        data: { title: 'Discover', screen: 'v-discover' },
      },
      {
        path: 'vendor/:id',
        loadComponent: () => import('@modules/customer/vendor-detail/customer-vendor-detail.component').then(m => m.CustomerVendorDetailComponent),
        data: { title: 'Vendor', screen: 'v-cvendor' },
      },
      {
        // Booking CREATE flow is login-gated — a guest is bounced to /auth/login with a
        // returnUrl and comes straight back here after signing in.
        path: 'book/:id',
        canActivate: [authGuard],
        loadComponent: () => import('@modules/customer/book/customer-book.component').then(m => m.CustomerBookComponent),
        data: { title: 'Request booking', screen: 'v-cbook' },
      },
      {
        path: 'bookings',
        loadComponent: () => import('@modules/customer/bookings/customer-bookings.component').then(m => m.CustomerBookingsComponent),
        data: { title: 'My bookings', screen: 'v-cbookings' },
      },
      {
        path: 'bookings/:id',
        loadComponent: () => import('@modules/customer/booking-view/customer-booking-view.component').then(m => m.CustomerBookingViewComponent),
        data: { title: 'Customer view', screen: 'v-custview' },
      },
      {
        path: 'profile',
        loadComponent: () => import('@modules/customer/profile/customer-profile.component').then(m => m.CustomerProfileComponent),
        data: { title: 'Profile', screen: 'v-cprofile' },
      },
      {
        path: 'invite',
        loadComponent: () => import('@modules/customer/invite/customer-invite.component').then(m => m.CustomerInviteComponent),
        data: { title: 'Majlis', screen: 'v-cinvite' },
      },
      {
        // Viewing an existing/shared card (?cardId=…) is public; the bare path is the
        // login-gated "create new card" intent (inviteCreateGuard).
        path: 'card',
        canActivate: [inviteCreateGuard],
        loadComponent: () => import('@modules/customer/card/customer-card.component').then(m => m.CustomerCardComponent),
        data: { title: 'My card', screen: 'v-kcard' },
      },
      {
        path: 'rsvp',
        canActivate: [inviteCreateGuard],
        loadComponent: () => import('@modules/customer/rsvp/customer-rsvp.component').then(m => m.CustomerRsvpComponent),
        data: { title: 'RSVP', screen: 'v-krsvp' },
      },
      {
        // Viewing an existing/shared checklist (?cardId=…) is public; the bare path is the
        // login-gated "new checklist" intent.
        path: 'checklist',
        canActivate: [inviteCreateGuard],
        loadComponent: () => import('@modules/customer/checklist/customer-checklist.component').then(m => m.CustomerChecklistComponent),
        data: { title: 'Checklist', screen: 'v-kcheck' },
      },
    ],
  },

  // PREVIEW (dev only — shared component & pipe gallery)
  {
    path: 'preview',
    loadComponent: () => import('@modules/shared/component-preview/component-preview.component').then(m => m.ComponentPreviewComponent),
    data: { title: 'Component preview' },
  },

  // FALLBACK — unknown paths land on the public marketplace, not the login screen.
  { path: '**', redirectTo: '/customer/discover' },
];
